<template>
  <main>
    <h3 class="d-none d-md-block">VENTAS DE SUCURSALES</h3>
    <h3 class="d-md-none font-weight-bold">VENTAS <br />DE SUCURSALES</h3>
    
    <!-- Descripción del módulo -->
    <div class="alert d-flex align-items-center mb-4" role="alert" style="background-color: #66C4DE; border-color: #66C4DE;">
      <i class="fas fa-info-circle fa-2x" style="margin-right: 15px; color: #fff;"></i>
      <div>
        <p class="mb-0" style="color: #353535;">
          <strong>Gestiona y analiza todas las ventas realizadas en tus sucursales físicas.</strong> 
          Este módulo te permite consultar transacciones en tiempo real, aplicar filtros avanzados para 
          obtener insights valiosos sobre el comportamiento de compra, exportar datos completos a Excel 
          con múltiples hojas especializadas, y visualizar métricas clave a través de gráficos interactivos 
          que te ayudarán a tomar decisiones estratégicas para tu negocio.
        </p>
      </div>
    </div>
    
    <gym-sales-table
      ref="gymSalesTable"
      :branches.sync="branches"
      :statusCatalog.sync="statusDictionary"
      :paymentMethodsCatalog.sync="paymentMethodsDictionary"
      :saleStatusCatalog.sync="saleStatusDictionary"
      :closeStatusCatalog.sync="closeStatusDictionary"
      @search="handleSearch"
      @searchUnified="handleSearchFromTable"
      @fetchNewPage="handleFetchNewPage"
      @export="handleExportRecords"
      :records.sync="records"
      :loading="loading"
      :totalRecords.sync="totalRecords"
      :allFilteredRecords.sync="allFilteredRecords"
      :originalFilteredRecords.sync="originalFilteredRecords"
      :localPagination.sync="localPagination"
    ></gym-sales-table>
  </main>
</template>

<script>
import GymSalesTable from "./GymSales/GymSalesTable.vue";
import XLSXpopulate from "xlsx-populate";
import excelExportMixin from '@/mixins/excelExportMixin';

export default {
  components: {
    GymSalesTable,
  },
  mixins: [excelExportMixin],
  data() {
    return {
      // Catálogos actualizados v2.3
      statusDictionary: [],              // Mantener por compatibilidad
      paymentMethodsDictionary: [],      // estadosMediosPago
      saleStatusDictionary: [],          // estadosVenta ([CERRADA], [ABIERTA])
      closeStatusDictionary: [],         // estadosCierreVenta ([VIGENTE], [ANULADA])
      
      records: [],
      branches: [],                      // sucursales (nombres directos)
      downloadedRecordsCount: 0,
      loading: false,
      
      // NUEVO: Sistema de datos reales
      allFilteredRecords: [], // TODOS los registros filtrados
      originalFilteredRecords: [], // Copia de los datos originales sin filtros avanzados
      
      // Paginación local
      localPagination: {
        currentPage: 1,
        itemsPerPage: 20,
        totalPages: 1
      },
      // Nuevo: persistir últimos filtros usados para paginación por servidor
      lastSearchFilters: null,
      
      // KPIs calculados
      totalRecords: 0,
      totalFilteredRecords: 0,
      totalSalesAmount: 0,
      totalValidSales: 0,
      totalCancelledSales: 0,
      totalClosedSales: 0,
    };
  },
  methods: {
    // Nuevo: mapear filtros UI → Parameters API
    mapFiltersToParameters(searchParams) {
      const Parameters = {};
      // nombreSucursal (opcional)
      if (searchParams.branch) {
        const branchName = this.getBranchNameById(searchParams.branch);
        if (branchName) Parameters.nombreSucursal = branchName;
      }
      // estadoVenta (mapeo especial)
      if (searchParams.status) {
        if (searchParams.status === '[VIGENTE]') Parameters.estadoVenta = '';
        else if (searchParams.status === '[ANULADA]') Parameters.estadoVenta = '[ANULADA]';
        else Parameters.estadoVenta = searchParams.status;
      }
      // método de pago (opcional)
      if (searchParams.paymentMethod) Parameters.metodoPago = searchParams.paymentMethod;
      // caja / POS (opcional)
      if (searchParams.cashier) Parameters.cajaPuntoDeVenta = searchParams.cashier;
      // tipo de plan (opcional)
      if (searchParams.planType) Parameters.planType = searchParams.planType;
      // fechas (DD-MM-YYYY) opcionales
      if (searchParams.dateFrom) Parameters.fechaDesde = this.formatDateForAPI(searchParams.dateFrom);
      if (searchParams.dateTo) Parameters.fechaHasta = this.formatDateForAPI(searchParams.dateTo);
      // vendedor (opcional)
      if (searchParams.seller) Parameters.nombreVendedor = searchParams.seller;
      if (searchParams.sellerName) Parameters.nombreVendedor = searchParams.sellerName;
      // cliente (opcional)
      if (searchParams.clientName) Parameters.nombreCliente = searchParams.clientName;
      // montos (opcionales)
      if (searchParams.amountFrom !== '' && searchParams.amountFrom != null) Parameters.amountFrom = searchParams.amountFrom;
      if (searchParams.amountTo !== '' && searchParams.amountTo != null) Parameters.amountTo = searchParams.amountTo;
      // alternativos avanzados
      if (!Parameters.metodoPago && searchParams.advancedPaymentMethod) Parameters.metodoPago = searchParams.advancedPaymentMethod;
      if (!Parameters.nombreSucursal && searchParams.advancedBranch) Parameters.nombreSucursal = searchParams.advancedBranch;
      if (!Parameters.estadoVenta && searchParams.advancedStatus) Parameters.estadoVenta = searchParams.advancedStatus;
      // búsqueda inteligente
      if (searchParams.searchText && searchParams.searchText.trim()) {
        const searchText = searchParams.searchText.trim();
        if (!isNaN(searchText) && searchText.length > 0) Parameters.idTransaccion = searchText;
        else if (searchText.includes('-') && searchText.length >= 9) Parameters.rutCliente = searchText;
        else Parameters.nombreCliente = searchText;
      }
      return Parameters;
    },

    // Nuevo: mapear filtros UI → Parameters específicos de MÉTRICAS
    mapFiltersToMetricsParameters(searchParams) {
      const MetricsParams = {};
      // Fechas en DD-MM-YYYY
      if (searchParams.dateFrom) MetricsParams.dateFrom = this.formatDateForAPI(searchParams.dateFrom);
      if (searchParams.dateTo) MetricsParams.dateTo = this.formatDateForAPI(searchParams.dateTo);
      // Filtros básicos
      if (searchParams.branch) MetricsParams.branch = searchParams.branch;
      if (searchParams.paymentMethod) MetricsParams.paymentMethod = searchParams.paymentMethod;
      // Estado: métricas esperan [VIGENTE] | [ANULADA] | ''
      if (typeof searchParams.status === 'string') MetricsParams.saleStatus = searchParams.status;
      if (!MetricsParams.saleStatus && searchParams.advancedStatus) MetricsParams.saleStatus = searchParams.advancedStatus;
      // Búsqueda general
      if (searchParams.searchText && searchParams.searchText.trim()) MetricsParams.textSearch = searchParams.searchText.trim();
      // Avanzados
      if (searchParams.sellerName) MetricsParams.sellerName = searchParams.sellerName;
      else if (searchParams.seller) MetricsParams.sellerName = searchParams.seller;
      if (searchParams.clientName) MetricsParams.clientName = searchParams.clientName;
      if (searchParams.cashier) MetricsParams.cashier = searchParams.cashier;
      if (searchParams.planType) MetricsParams.planType = searchParams.planType;
      if (searchParams.amountFrom) MetricsParams.amountFrom = searchParams.amountFrom;
      if (searchParams.amountTo) MetricsParams.amountTo = searchParams.amountTo;
      // Alternativos avanzados conocidos
      if (!MetricsParams.paymentMethod && searchParams.advancedPaymentMethod) MetricsParams.paymentMethod = searchParams.advancedPaymentMethod;
      if (!MetricsParams.branch && searchParams.advancedBranch) MetricsParams.branch = searchParams.advancedBranch;
      return MetricsParams;
    },

    // Nuevo: obtener KPIs y gráficos desde backend
    async fetchMetricsForFilters(metricsParameters) {
      const response = await this.$CentralGymOPS_API.post(
        "",
        {
          Header: { Operacion: "/v1/sales/get/gymsales/metrics" },
          Auth: { Token: sessionStorage.getItem("jCxbraoQeD") },
          Parameters: metricsParameters,
          Body: {}
        },
        { headers: { "Content-Type": "application/json" } }
      );
      if (!response.data || !response.data.result) throw new Error('Respuesta de métricas inválida');
      return response.data.result;
    },
    // Método helper para parsear valores monetarios en formato chileno
    parseMonetaryValue(value) {
      if (typeof value === 'number') return value;
      if (!value) return 0;
      
      // Convertir a string y limpiar espacios
      let stringValue = String(value).trim();
      
      // Remover símbolo de moneda si existe
      stringValue = stringValue.replace(/[$]/g, '');
      
      // Si el valor contiene puntos y comas, asumir formato chileno (1.500.000,50)
      if (stringValue.includes('.') && stringValue.includes(',')) {
        // Formato chileno: punto para miles, coma para decimales
        stringValue = stringValue.replace(/\./g, '').replace(',', '.');
      } else if (stringValue.includes('.') && !stringValue.includes(',')) {
        // Verificar si es formato chileno sin decimales (1.500.000) o formato US (1500.50)
        const parts = stringValue.split('.');
        if (parts.length === 2 && parts[1].length > 2) {
          // Probablemente formato chileno (1.500.000)
          stringValue = stringValue.replace(/\./g, '');
        }
        // Si tiene 2 decimales o menos, asumir formato US
      } else if (stringValue.includes(',') && !stringValue.includes('.')) {
        // Solo comas, puede ser formato US (1,500) o chileno con decimales (1500,50)
        const parts = stringValue.split(',');
        if (parts.length === 2 && parts[1].length <= 2) {
          // Formato chileno con decimales (1500,50)
          stringValue = stringValue.replace(',', '.');
        } else {
          // Formato US con separador de miles (1,500)
          stringValue = stringValue.replace(/,/g, '');
        }
      }
      
      const parsed = parseFloat(stringValue);
      return isNaN(parsed) ? 0 : parsed;
    },
    
    // ========================================
    // MÉTODO DE EXPORTACIÓN MEJORADO (compatible con OnLineSalesTable y GymSalesTable)
    // ========================================
    async handleExportRecords(statusOrFilters, paymentMethod, branch, dateFrom, dateTo, searchText) {
      this.$logger.log('📊 Exportación solicitada con parámetros:', arguments);
      
      // Determinar si recibimos parámetros individuales (nuevo formato) o un objeto filters (formato anterior)
      let filters = {};
      
      if (typeof statusOrFilters === 'object' && statusOrFilters !== null) {
        // Formato anterior: recibimos un objeto filters
        filters = statusOrFilters;
        this.$logger.log('📊 Usando formato de objeto filters:', filters);
      } else {
        // Nuevo formato: recibimos parámetros individuales como OnLineSalesTable
        filters = {
          status: statusOrFilters,
          paymentMethod: paymentMethod,
          branch: branch,
          dateFrom: dateFrom,
          dateTo: dateTo,
          searchText: searchText
        };
        this.$logger.log('📊 Usando formato de parámetros individuales:', filters);
      }
      
      this.$swal.fire({
        title: "Cargando datos para exportación!!",
        text: `Preparando exportación de ventas del gimnasio...`,
        showConfirmButton: false,
        allowOutsideClick: false,
        didOpen: () => {
          this.$swal.showLoading();
        },
      });

      try {
        // OPTIMIZADO v3.0: Usar versión asíncrona para evitar timeouts
        const data = await this.createGymSalesRecordsExportDataAsync(filters);
        const template = await this.fetchExcelTemplate("RegistroVentasGimnasio");
        await this.generateExcelFileAsync(data, template);
        
        this.$logger.log('✅ Exportación completada exitosamente. Registros exportados:', data.length);
      } catch (error) {
        this.$logger.error('❌ Error en exportación:', error);
        this.$swal.fire({
          icon: "error",
          title: "Error al exportar!",
          text: `${error.message || error}. Vuelve a intentarlo mas tarde, si el problema persiste ponte en contacto con soporte.`,
          showConfirmButton: true,
          confirmButtonColor: "#6bd098",
        });
      }
    },

    async createGymSalesRecordsExportData(filters) {
      this.$logger.log('📊 Preparando parámetros para exportación:', filters);
      
      // Convertir filtros del componente a parámetros de API
      let Parameters = {};
      
      // Mapear filtros a parámetros de API con los nombres correctos
      if (filters.status) {
        if (filters.status === '[VIGENTE]') {
          Parameters.estadoVenta = ""; // VIGENTE = cadena vacía
        } else if (filters.status === '[ANULADA]') {
          Parameters.estadoVenta = "[ANULADA]"; // ANULADA = tal como viene
        } else {
          Parameters.estadoVenta = filters.status;
        }
      }
      
      if (filters.paymentMethod) {
        Parameters.metodoPago = filters.paymentMethod;
      }
      
      if (filters.branch) {
        Parameters.nombreSucursal = filters.branch;
      }
      
      if (filters.dateFrom) {
        Parameters.fechaDesde = filters.dateFrom;
      }
      
      if (filters.dateTo) {
        Parameters.fechaHasta = filters.dateTo;
      }
      
      // Búsqueda inteligente por texto
      if (filters.searchText && filters.searchText.trim()) {
        const searchText = filters.searchText.trim();
        if (!isNaN(searchText) && searchText.length > 0) {
          Parameters.idTransaccion = searchText;
        } else if (searchText.includes('-') && searchText.length >= 9) {
          Parameters.rutCliente = searchText;
        } else {
          Parameters.nombreCliente = searchText;
        }
      }
      
      this.$logger.log('📊 Parámetros de API preparados:', Parameters);
      
      let keepFetching = true;
      let page = 0;
      let data = [];

      this.$swal.fire({
        title: "Descargando registros de ventas...",
        text: `Registros descargados: ${data.length}`,
        timerProgressBar: true,
        allowOutsideClick: false,
        showConfirmButton: false,
        didOpen: () => {
          this.$swal.showLoading();
        },
      });

      do {
        try {
          this.$logger.log(`📊 Descargando página ${page}...`);
          
          // ✅ NUEVO FORMATO v2.4: Body con Header.Operacion y Auth.Token
          const response = await this.$CentralGymOPS_API.post(
            "",
            {
              Header: {
                Operacion: "/v1/sales/get/gymsales/reportformat" // Endpoint específico para reportes
              },
              Auth: {
                Token: sessionStorage.getItem("jCxbraoQeD")
              },
              Parameters: { ...Parameters, page: page },
              Body: {}
            },
            {
              headers: {
                "Content-Type": "application/json"
              }
            }
          );

          if (response.data && response.data.result) {
            data.push(...response.data.result);
            this.downloadedRecordsCount = data.length;
            page++;
            keepFetching = response.data.result.length === 100; // Continúa si recibió 100 registros
            this.$swal.update({
              text: `Registros descargados: ${data.length}`,
            });
            this.$logger.log(`📊 Página ${page - 1}: ${response.data.result.length} registros obtenidos. Total: ${data.length}`);
          } else {
            this.$logger.log('📊 No se recibieron más datos, finalizando descarga');
            keepFetching = false;
          }
        } catch (error) {
          this.$logger.error(`❌ Error en página ${page}:`, error);
          throw error;
        }
      } while (keepFetching);

      this.$logger.log('✅ Descarga completada. Total de registros:', data.length);
      
      // 🔍 ANÁLISIS DE ELEMENTOS MÚLTIPLES
      let ventasConMultiplesElementos = 0;
      let totalFormasPago = 0;
      let totalProductos = 0;
      let totalPlanes = 0;
      
      data.forEach(record => {
        const formasPago = record.infoVenta?.venta?.length || 0;
        const productos = record.infoVenta?.detalleVenta?.length || 0;
        const planes = record.infoVenta?.plan?.length || 0;
        
        totalFormasPago += formasPago;
        totalProductos += productos;
        totalPlanes += planes;
        
        if (formasPago > 1 || productos > 1 || planes > 1) {
          ventasConMultiplesElementos++;
          this.$logger.log(`📊 Venta #${record.idTransaccion} con múltiples elementos:`, {
            formasPago: formasPago,
            productos: productos,
            planes: planes
          });
        }
      });
      
      this.$logger.log('📊 RESUMEN DE ELEMENTOS MÚLTIPLES:', {
        totalVentas: data.length,
        ventasConMultiplesElementos: ventasConMultiplesElementos,
        promedioFormasPagoPorVenta: (totalFormasPago / data.length).toFixed(2),
        promedioProductosPorVenta: (totalProductos / data.length).toFixed(2),
        promedioPlanePorVenta: (totalPlanes / data.length).toFixed(2),
        beneficioExportacionMejorada: ventasConMultiplesElementos > 0 ? '✅ SÍ - Datos adicionales serán exportados' : '➖ No hay datos adicionales en este dataset'
      });
      
      return data;
    },

        async generateExcelFile(data, template) {
      this.$swal.update({
        title: "Generando reporte multi-hoja...",
        text: "Generando reporte completo con hojas especializadas",
      });
      this.$swal.showLoading();

      try {
        await XLSXpopulate.fromDataAsync(template).then(async (wb) => {
          // 📋 OBTENER TODAS LAS HOJAS
          const consolidadoSheet = wb.sheet(0); // Hoja 1: CONSOLIDADO
          const ventasSheet = wb.sheet(1);      // Hoja 2: VENTAS  
          const controlSheet = wb.sheet(2);     // Hoja 3: CONTROL Y DETALLE
          const productosSheet = wb.sheet(3);   // Hoja 4: PRODUCTOS
          const membresiasSheet = wb.sheet(4);  // Hoja 5: MEMBRESIAS
          const dteSheet = wb.sheet(5);         // Hoja 6: DTE (Documentos Tributarios)

          // 🕒 CONFIGURAR FECHA DE GENERACIÓN EN TODAS LAS HOJAS
          const fechaGeneracion = this.$moment().format("DD/MM/YYYY - HH:mm:ss");
          consolidadoSheet.cell("D3").value(fechaGeneracion);
          ventasSheet.cell("D3").value(fechaGeneracion);
          controlSheet.cell("D3").value(fechaGeneracion);
          productosSheet.cell("D3").value(fechaGeneracion);
          membresiasSheet.cell("D3").value(fechaGeneracion);
          dteSheet.cell("D3").value(fechaGeneracion);

          // 🔄 CONTADORES DE FILAS PARA CADA HOJA
          let consolidadoRow = 7;
          let ventasRow = 7;
          let controlRow = 7;
          let productosRow = 7;
          let membresiasRow = 7;
          let dteRow = 7;

          this.$logger.log('📊 Iniciando exportación multi-hoja:', {
            totalVentas: data.length,
            hojas: ['CONSOLIDADO', 'VENTAS', 'CONTROL Y DETALLE', 'PRODUCTOS', 'MEMBRESIAS', 'DTE']
          });

                    data.forEach((record, recordIndex) => {
            // ========================================
            // 📋 HOJA 1: CONSOLIDADO (MANTENER ACTUAL)
            // ========================================
            this.fillConsolidadoSheet(consolidadoSheet, record, consolidadoRow);
            consolidadoRow++;

            // ========================================
            // 📋 HOJA 2: VENTAS (Una fila por venta)
            // ========================================
            this.fillVentasSheet(ventasSheet, record, ventasRow);
            ventasRow++;

            // ========================================
            // 📋 HOJA 3: CONTROL Y DETALLE (Una fila por forma de pago)
            // ========================================
            const formasPago = record.infoVenta?.venta || [];
            if (formasPago.length > 0) {
              formasPago.forEach(formaPago => {
                this.fillControlSheet(controlSheet, record, formaPago, controlRow);
                controlRow++;
              });
            } else {
              // Si no hay formas de pago, crear una fila vacía con solo info básica
              this.fillControlSheet(controlSheet, record, {}, controlRow);
              controlRow++;
            }

            // ========================================
            // 📋 HOJA 4: PRODUCTOS (Una fila por producto)
            // ========================================
            const productos = record.infoVenta?.detalleVenta || [];
            if (productos.length > 0) {
              productos.forEach(producto => {
                this.fillProductosSheet(productosSheet, record, producto, productosRow);
                productosRow++;
              });
            } else {
              // Si no hay productos, crear una fila vacía con solo ID
              this.fillProductosSheet(productosSheet, record, {}, productosRow);
              productosRow++;
            }

            // ========================================
            // 📋 HOJA 5: MEMBRESIAS (Una fila por plan)
            // ========================================
            const planes = record.infoVenta?.plan || [];
            if (planes.length > 0) {
              planes.forEach(plan => {
                this.fillMembresiasSheet(membresiasSheet, record, plan, membresiasRow);
                membresiasRow++;
              });
            } else {
              // Si no hay planes, crear una fila vacía con solo ID
              this.fillMembresiasSheet(membresiasSheet, record, {}, membresiasRow);
              membresiasRow++;
            }

            // ========================================
            // 📋 HOJA 6: DTE (Una fila por documento tributario)
            // ========================================
            if (record.dte?.bsale) {
              const dte = record.dte.bsale;
              
              // Procesar Factura Electrónica si existe
              if (dte.facturaElectronica && Object.keys(dte.facturaElectronica).length > 0) {
                this.fillDTESheet(dteSheet, record, 'FACTURA', dte.facturaElectronica, dteRow);
                dteRow++;
              }
              
              // Procesar Boleta Electrónica si existe
              if (dte.boletaElectronica && Object.keys(dte.boletaElectronica).length > 0) {
                this.fillDTESheet(dteSheet, record, 'BOLETA', dte.boletaElectronica, dteRow);
                dteRow++;
              }
              
              // Procesar Nota de Crédito si existe
              if (dte.notaCredito && Object.keys(dte.notaCredito).length > 0) {
                this.fillDTESheet(dteSheet, record, 'NOTA_CREDITO', dte.notaCredito, dteRow);
                dteRow++;
              }
            }
          });

          this.$logger.log('📊 Exportación multi-hoja completada:', {
            filasConsolidado: consolidadoRow - 7,
            filasVentas: ventasRow - 7,
            filasControl: controlRow - 7,
            filasProductos: productosRow - 7,
            filasMembresias: membresiasRow - 7,
            filasDTE: dteRow - 7
          });

          await wb
            .outputAsync()
            .then((blob) => {
              if (window.navigator && window.navigator.msSaveOrOpenBlob) {
                window.navigator.msSaveOrOpenBlob(
                  blob,
                  `Registro Ventas Gimnasio - ${this.$moment().format(
                    "DD-MM-yyyy - HH-mm-ss"
                  )}.xlsx`
                );
              } else {
                var url = window.URL.createObjectURL(blob);
                var a = document.createElement("a");
                document.body.appendChild(a);
                a.href = url;
                a.download = `Registro Ventas Gimnasio - ${this.$moment().format(
                  "DD-MM-yyyy - HH-mm-ss"
                )}.xlsx`;
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
              }
              this.$swal.close();
            })
            .catch((error) => {
              this.$logger.error(error);
              this.$swal.fire({
                title: "Error!",
                text: error.message ||
                  "Ha ocurrido un error durante la renderización del reporte",
                confirmButtonColor: "#6bd098",
                icon: "error",
              });
            });
        });
      } catch (error) {
        this.$logger.error(error);
        this.$swal.fire({
          title: "Error!",
          text: error.message ||
            "Ha ocurrido un error durante la generación del archivo Excel",
          confirmButtonColor: "#6bd098",
          icon: "error",
        });
      }
    },

    // ========================================
    // MÉTODOS AUXILIARES PARA HOJAS ESPECIALIZADAS
    // ========================================

    fillConsolidadoSheet(sheet, record, row) {
      // 📋 HOJA 1: CONSOLIDADO - Actualizada con todos los campos nuevos del endpoint enriquecido
      
      // INFORMACIÓN GENERAL (Columnas C-N)
      if (record.idTransaccion) sheet.cell(`C${row}`).value(record.idTransaccion);
      if (record.fechaHora) sheet.cell(`D${row}`).value(record.fechaHora);
      if (record.nombreEmpresa) sheet.cell(`E${row}`).value(record.nombreEmpresa);
      if (record.nombreSucursal) sheet.cell(`F${row}`).value(record.nombreSucursal);
      if (record.nombreVendedor) sheet.cell(`G${row}`).value(record.nombreVendedor);
      if (record.cajaPuntoDeVenta) sheet.cell(`H${row}`).value(record.cajaPuntoDeVenta);
      if (record.metodoPago) sheet.cell(`I${row}`).value(record.metodoPago);
      if (record.rutCliente) sheet.cell(`J${row}`).value(record.rutCliente);
      if (record.nombreCliente) sheet.cell(`K${row}`).value(record.nombreCliente);
      if (record.correoCliente) sheet.cell(`L${row}`).value(record.correoCliente);
      if (record.porcentajeIva) sheet.cell(`M${row}`).value(record.porcentajeIva);
                  if (record.ventaTotal) sheet.cell(`N${row}`).value(this.parseMonetaryValue(record.ventaTotal));
      
      // CONTROL Y DETALLE (Columnas O-X)
      if (record.estadoVenta) sheet.cell(`O${row}`).value(record.estadoVenta);
      if (record.estadoCierre) sheet.cell(`P${row}`).value(record.estadoCierre);
      if (record.fechaCierre) sheet.cell(`Q${row}`).value(record.fechaCierre);
      if (record.responsableCierre) sheet.cell(`R${row}`).value(record.responsableCierre);
      if (record.fechaAnulacion) sheet.cell(`S${row}`).value(record.fechaAnulacion);
      if (record.responsableAnulacion) sheet.cell(`T${row}`).value(record.responsableAnulacion);
      if (record.causalAnulacion) sheet.cell(`U${row}`).value(record.causalAnulacion);
      
      // Formas de pago concatenadas
      if (record.infoVenta?.venta?.length > 0) {
        const formasPago = record.infoVenta.venta.map(v => v.formaPago).filter(Boolean).join(' | ');
        const montosPago = record.infoVenta.venta.map(v => `$${parseFloat(v.montoVenta || 0).toLocaleString('es-CL')}`).join(' | ');
        if (formasPago) sheet.cell(`V${row}`).value(formasPago);
        if (montosPago) sheet.cell(`W${row}`).value(montosPago);
      }
      
      if (record.utilizarInventario !== undefined) {
        sheet.cell(`X${row}`).value(record.utilizarInventario ? 'SI' : 'NO');
      }
      
      // PRODUCTOS (Columnas Y-AJ) - Concatenados
      if (record.infoVenta?.detalleVenta?.length > 0) {
        const detalleVenta = record.infoVenta.detalleVenta;
        const codigos = detalleVenta.map(p => p.codigo).filter(Boolean).join(' | ');
        const detalles = detalleVenta.map(p => p.detalle).filter(Boolean).join(' | ');
        const categorias = detalleVenta.map(p => p.categoriaProducto).filter(Boolean).join(' | ');
        const cantidades = detalleVenta.map(p => p.cantidad || 0).join(' | ');
        const preciosUnitariosNetos = detalleVenta.map(p => `$${parseFloat(p.precioUnitarioNeto || 0).toLocaleString('es-CL')}`).join(' | ');
        const preciosUnitariosBrutos = detalleVenta.map(p => `$${parseFloat(p.precioUnitarioBruto || 0).toLocaleString('es-CL')}`).join(' | ');
        const porcentajesDescuento = detalleVenta.map(p => p.porcentajeDescuento ? `${p.porcentajeDescuento}%` : '0%').join(' | ');
        const montosIva = detalleVenta.map(p => `$${parseFloat(p.montoIva || 0).toLocaleString('es-CL')}`).join(' | ');
        const totalesNetos = detalleVenta.map(p => `$${parseFloat(p.totalNeto || 0).toLocaleString('es-CL')}`).join(' | ');
        const totales = detalleVenta.map(p => `$${parseFloat(p.precio || 0).toLocaleString('es-CL')}`).join(' | ');
        const tiposIva = detalleVenta.map(p => p.tipoIva || '').join(' | ');
        const tiposItem = detalleVenta.map(p => p.tipoItem || '').join(' | ');
        
        if (codigos) sheet.cell(`Y${row}`).value(codigos);
        if (detalles) sheet.cell(`Z${row}`).value(detalles);
        if (categorias) sheet.cell(`AA${row}`).value(categorias);
        if (cantidades) sheet.cell(`AB${row}`).value(cantidades);
        if (preciosUnitariosNetos) sheet.cell(`AC${row}`).value(preciosUnitariosNetos);
        if (preciosUnitariosBrutos) sheet.cell(`AD${row}`).value(preciosUnitariosBrutos);
        if (porcentajesDescuento) sheet.cell(`AE${row}`).value(porcentajesDescuento);
        if (montosIva) sheet.cell(`AF${row}`).value(montosIva);
        if (totalesNetos) sheet.cell(`AG${row}`).value(totalesNetos);
        if (totales) sheet.cell(`AH${row}`).value(totales);
        if (tiposIva) sheet.cell(`AI${row}`).value(tiposIva);
        if (tiposItem) sheet.cell(`AJ${row}`).value(tiposItem);
      }
      
      // MEMBRESÍAS (Columnas AK-BZ) - Concatenadas con TODOS los campos
      if (record.infoVenta?.plan?.length > 0) {
        const planes = record.infoVenta.plan;
        
        // Información básica (AK-AO)
        const descripciones = planes.map(p => p.descripcionPlan).filter(Boolean).join(' | ');
        const familias = planes.map(p => p.familiaPlan).filter(Boolean).join(' | ');
        const duraciones = planes.map(p => p.duracion || '').join(' | ');
        const unidades = planes.map(p => p.unidad || '').join(' | ');
        const estados = planes.map(p => p.estadoDePlan).filter(Boolean).join(' | ');
        
        // Configuración del plan (AP-AW)
        const multiSedes = planes.map(p => p.multiSede !== undefined ? (p.multiSede ? 'SI' : 'NO') : '').join(' | ');
        const montos = planes.map(p => p.monto !== undefined ? `$${parseFloat(p.monto).toLocaleString('es-CL')}` : '').join(' | ');
        const matriculas = planes.map(p => p.matricula !== undefined ? (p.matricula ? 'SI' : 'NO') : '').join(' | ');
        const montosMatricula = planes.map(p => p.montoMatricula !== undefined ? `$${parseFloat(p.montoMatricula).toLocaleString('es-CL')}` : '').join(' | ');
        const sesiones = planes.map(p => p.sesiones !== undefined ? (p.sesiones ? 'SI' : 'NO') : '').join(' | ');
        const nSesiones = planes.map(p => p.nSesiones || '').join(' | ');
        const sesionesUtilizadas = planes.map(p => p.sesionesUtilizadas || '').join(' | ');
        const permiteRenovaciones = planes.map(p => p.permiteRenovacion !== undefined ? (p.permiteRenovacion ? 'SI' : 'NO') : '').join(' | ');
        
        // Configuración adicional (AX-BE)
        const expiracionesRenovacion = planes.map(p => p.expiracionRenovacion || '').join(' | ');
        const codigosTurno = planes.map(p => p.codigoTurno || '').join(' | ');
        const facturas = planes.map(p => p.factura !== undefined ? (p.factura ? 'SI' : 'NO') : '').join(' | ');
        const congelares = planes.map(p => p.congelar || '').join(' | ');
        const mesesCalendario = planes.map(p => p.mesCalendario !== undefined ? (p.mesCalendario ? 'SI' : 'NO') : '').join(' | ');
        const idsFamilia = planes.map(p => p.idFamilia || '').join(' | ');
        const idsSubFamilia = planes.map(p => p.idSubFamilia || '').join(' | ');
        const idsPlan = planes.map(p => p.idPlan || '').join(' | ');
        
        // Clasificación y tipo (BF-BK)
        const modalidades = planes.map(p => p.modalidad || '').join(' | ');
        const tiposDePlan = planes.map(p => p.tipoDePlan || '').join(' | ');
        const tiposPlanPagado = planes.map(p => p.tipoPlanPagado || '').join(' | ');
        const subFamiliasPlan = planes.map(p => p.subFamiliaPlan || '').join(' | ');
        const descuentos = planes.map(p => p.dcto || '').join(' | ');
        const tiposIva = planes.map(p => p.tipoIva || '').join(' | ');
        
        // Fechas importantes (BL-BR)
        const fechasIngreso = planes.map(p => p.fechaDeIngreso || '').join(' | ');
        const fechasInicio = planes.map(p => p.fechaDeInicio || '').join(' | ');
        const fechasVencimiento = planes.map(p => p.fechaDeVencimiento || '').join(' | ');
        const fechasVigencia = planes.map(p => p.fechaDeVigencia || '').join(' | ');
        const fechasUltimoPago = planes.map(p => p.fechaUltimoPago || '').join(' | ');
        const horasUltimoPago = planes.map(p => p.horaUltimoPago || '').join(' | ');
        const fechasPagoMatricula = planes.map(p => p.fechaPagoMatricula || '').join(' | ');
        
        // Información adicional (BS-CL)
        const unidadesExpiracion = planes.map(p => p.unidadExpiracionRenovacion || '').join(' | ');
        const turnos = planes.map(p => p.turno || '').join(' | ');
        const sucursalesUltimoPago = planes.map(p => p.sucursalUltimoPago || '').join(' | ');
        const sucursalesUltimoAcceso = planes.map(p => p.sucursalUltimoAcceso || '').join(' | ');
        const fechasHoraUltimoAcceso = planes.map(p => p.fechaHoraUltimoAcceso || '').join(' | ');
        const nombresSucursalAsignacion = planes.map(p => p.nombreSucursalAsignacion || '').join(' | ');
        const nombresUsuarioAsignacion = planes.map(p => p.nombreUsuarioAsignacion || '').join(' | ');
        const estadosProcesosPago = planes.map(p => p.estadoProcesoPago || '').join(' | ');
        
        // Escribir en celdas - Información básica
        if (descripciones) sheet.cell(`AK${row}`).value(descripciones);
        if (familias) sheet.cell(`AL${row}`).value(familias);
        if (duraciones) sheet.cell(`AM${row}`).value(duraciones);
        if (unidades) sheet.cell(`AN${row}`).value(unidades);
        if (estados) sheet.cell(`AO${row}`).value(estados);
        
        // Configuración del plan
        if (multiSedes) sheet.cell(`AP${row}`).value(multiSedes);
        if (montos) sheet.cell(`AQ${row}`).value(montos);
        if (matriculas) sheet.cell(`AR${row}`).value(matriculas);
        if (montosMatricula) sheet.cell(`AS${row}`).value(montosMatricula);
        if (sesiones) sheet.cell(`AT${row}`).value(sesiones);
        if (nSesiones) sheet.cell(`AU${row}`).value(nSesiones);
        if (sesionesUtilizadas) sheet.cell(`AV${row}`).value(sesionesUtilizadas);
        if (permiteRenovaciones) sheet.cell(`AW${row}`).value(permiteRenovaciones);
        
        // Configuración adicional
        if (expiracionesRenovacion) sheet.cell(`AX${row}`).value(expiracionesRenovacion);
        if (codigosTurno) sheet.cell(`AY${row}`).value(codigosTurno);
        if (facturas) sheet.cell(`AZ${row}`).value(facturas);
        if (congelares) sheet.cell(`BA${row}`).value(congelares);
        if (mesesCalendario) sheet.cell(`BB${row}`).value(mesesCalendario);
        if (idsFamilia) sheet.cell(`BC${row}`).value(idsFamilia);
        if (idsSubFamilia) sheet.cell(`BD${row}`).value(idsSubFamilia);
        if (idsPlan) sheet.cell(`BE${row}`).value(idsPlan);
        
        // Clasificación y tipo
        if (modalidades) sheet.cell(`BF${row}`).value(modalidades);
        if (tiposDePlan) sheet.cell(`BG${row}`).value(tiposDePlan);
        if (tiposPlanPagado) sheet.cell(`BH${row}`).value(tiposPlanPagado);
        if (subFamiliasPlan) sheet.cell(`BI${row}`).value(subFamiliasPlan);
        if (descuentos) sheet.cell(`BJ${row}`).value(descuentos);
        if (tiposIva) sheet.cell(`BK${row}`).value(tiposIva);
        
        // Fechas importantes
        if (fechasIngreso) sheet.cell(`BL${row}`).value(fechasIngreso);
        if (fechasInicio) sheet.cell(`BM${row}`).value(fechasInicio);
        if (fechasVencimiento) sheet.cell(`BN${row}`).value(fechasVencimiento);
        if (fechasVigencia) sheet.cell(`BO${row}`).value(fechasVigencia);
        if (fechasUltimoPago) sheet.cell(`BP${row}`).value(fechasUltimoPago);
        if (horasUltimoPago) sheet.cell(`BQ${row}`).value(horasUltimoPago);
        if (fechasPagoMatricula) sheet.cell(`BR${row}`).value(fechasPagoMatricula);
        
        // Información adicional
        if (unidadesExpiracion) sheet.cell(`BS${row}`).value(unidadesExpiracion);
        if (turnos) sheet.cell(`BT${row}`).value(turnos);
        if (sucursalesUltimoPago) sheet.cell(`BU${row}`).value(sucursalesUltimoPago);
        if (sucursalesUltimoAcceso) sheet.cell(`BV${row}`).value(sucursalesUltimoAcceso);
        if (fechasHoraUltimoAcceso) sheet.cell(`BW${row}`).value(fechasHoraUltimoAcceso);
        if (nombresSucursalAsignacion) sheet.cell(`BX${row}`).value(nombresSucursalAsignacion);
        if (nombresUsuarioAsignacion) sheet.cell(`BY${row}`).value(nombresUsuarioAsignacion);
        if (estadosProcesosPago) sheet.cell(`BZ${row}`).value(estadosProcesosPago);
      }
    },

    fillVentasSheet(sheet, record, row) {
      // 📋 HOJA 2: VENTAS - Actualizada con todos los campos de información general
      // INFORMACIÓN GENERAL: ID Transacción | Fecha y Hora | Empresa | Sucursal | Vendedor | Caja / POS | Método de Pago | RUT Cliente | Nombre Cliente | Correo Cliente | Impuestos | Total Venta
      if (record.idTransaccion) sheet.cell(`C${row}`).value(record.idTransaccion);
      if (record.fechaHora) sheet.cell(`D${row}`).value(record.fechaHora);
      if (record.nombreEmpresa) sheet.cell(`E${row}`).value(record.nombreEmpresa);
      if (record.nombreSucursal) sheet.cell(`F${row}`).value(record.nombreSucursal);
      if (record.nombreVendedor) sheet.cell(`G${row}`).value(record.nombreVendedor);
      if (record.cajaPuntoDeVenta) sheet.cell(`H${row}`).value(record.cajaPuntoDeVenta);
      if (record.metodoPago) sheet.cell(`I${row}`).value(record.metodoPago);
      if (record.rutCliente) sheet.cell(`J${row}`).value(record.rutCliente);
      if (record.nombreCliente) sheet.cell(`K${row}`).value(record.nombreCliente);
      if (record.correoCliente) sheet.cell(`L${row}`).value(record.correoCliente);
      if (record.porcentajeIva) sheet.cell(`M${row}`).value(record.porcentajeIva);
                  if (record.ventaTotal) sheet.cell(`N${row}`).value(this.parseMonetaryValue(record.ventaTotal));
    },

    fillControlSheet(sheet, record, formaPago, row) {
      // 📋 HOJA 3: CONTROL Y DETALLE - Actualizada con todos los campos de control
      // CONTROL Y DETALLE: ID Transacción | Estado Venta | Estado Cierre | Fecha Cierre | Responsable Cierre | Fecha Anulación | Responsable Anulación | Causal de Anulación | Forma de Pago Detallada | Monto Forma de Pago | Utiliza Inventario
      
      // ID Transacción (para relacionar con otras hojas)
      if (record.idTransaccion) sheet.cell(`C${row}`).value(record.idTransaccion);
      
      // Estados y cierre
      if (record.estadoVenta) sheet.cell(`D${row}`).value(record.estadoVenta);
      if (record.estadoCierre) sheet.cell(`E${row}`).value(record.estadoCierre);
      if (record.fechaCierre) sheet.cell(`F${row}`).value(record.fechaCierre);
      if (record.responsableCierre) sheet.cell(`G${row}`).value(record.responsableCierre);
      
      // Información de anulación
      if (record.fechaAnulacion) sheet.cell(`H${row}`).value(record.fechaAnulacion);
      if (record.responsableAnulacion) sheet.cell(`I${row}`).value(record.responsableAnulacion);
      if (record.causalAnulacion) sheet.cell(`J${row}`).value(record.causalAnulacion);
      
      // Datos específicos de la forma de pago
      if (formaPago.formaPago) sheet.cell(`K${row}`).value(formaPago.formaPago);
      if (formaPago.montoVenta) sheet.cell(`L${row}`).value(parseFloat(formaPago.montoVenta || 0));
      
      // Utiliza inventario
      if (record.utilizarInventario !== undefined) {
        sheet.cell(`M${row}`).value(record.utilizarInventario ? 'SI' : 'NO');
      }
    },

    fillProductosSheet(sheet, record, producto, row) {
      // 📋 HOJA 4: PRODUCTOS - Actualizada con todos los campos detallados
      // PRODUCTOS: ID Transacción | Código Producto | Detalle Producto | Categoría Producto | Cantidad | Precio Unitario Neto | Precio Unitario Bruto | Porcentaje Descuento | Monto IVA | Total Neto | Total | Tipo IVA | Tipo Item
      
      // ID Transacción (para relacionar con otras hojas)
      if (record.idTransaccion) sheet.cell(`C${row}`).value(record.idTransaccion);
      
      // Información del producto
      if (producto.codigo) sheet.cell(`D${row}`).value(producto.codigo);
      if (producto.detalle) sheet.cell(`E${row}`).value(producto.detalle);
      if (producto.categoriaProducto) sheet.cell(`F${row}`).value(producto.categoriaProducto);
      if (producto.cantidad !== undefined) sheet.cell(`G${row}`).value(producto.cantidad);
      
      // Precios y descuentos
      if (producto.precioUnitarioNeto !== undefined) sheet.cell(`H${row}`).value(parseFloat(producto.precioUnitarioNeto));
      if (producto.precioUnitarioBruto !== undefined) sheet.cell(`I${row}`).value(parseFloat(producto.precioUnitarioBruto));
      if (producto.porcentajeDescuento !== undefined) sheet.cell(`J${row}`).value(producto.porcentajeDescuento);
      
      // IVA y totales
      if (producto.montoIva !== undefined) sheet.cell(`K${row}`).value(parseFloat(producto.montoIva));
      if (producto.totalNeto !== undefined) sheet.cell(`L${row}`).value(parseFloat(producto.totalNeto));
      if (producto.precio !== undefined) sheet.cell(`M${row}`).value(parseFloat(producto.precio)); // Total
      
      // Tipos
      if (producto.tipoIva) sheet.cell(`N${row}`).value(producto.tipoIva);
      if (producto.tipoItem) sheet.cell(`O${row}`).value(producto.tipoItem);
    },

    fillMembresiasSheet(sheet, record, plan, row) {
      // 📋 HOJA 5: MEMBRESIAS - Con TODOS los campos disponibles del plan
      
      // ID Transacción (columna C)
      if (record.idTransaccion) sheet.cell(`C${row}`).value(record.idTransaccion);
      
      // Información básica del plan (columnas D-H)
      if (plan.descripcionPlan) sheet.cell(`D${row}`).value(plan.descripcionPlan);
      if (plan.familiaPlan) sheet.cell(`E${row}`).value(plan.familiaPlan);
      if (plan.duracion !== undefined) sheet.cell(`F${row}`).value(plan.duracion);
      if (plan.unidad) sheet.cell(`G${row}`).value(plan.unidad);
      if (plan.estadoDePlan) sheet.cell(`H${row}`).value(plan.estadoDePlan);
      
      // Configuración del plan (columnas I-P)
      if (plan.multiSede !== undefined) sheet.cell(`I${row}`).value(plan.multiSede ? 'SI' : 'NO');
      if (plan.monto !== undefined) sheet.cell(`J${row}`).value(parseFloat(plan.monto));
      if (plan.matricula !== undefined) sheet.cell(`K${row}`).value(plan.matricula ? 'SI' : 'NO');
      if (plan.montoMatricula !== undefined) sheet.cell(`L${row}`).value(parseFloat(plan.montoMatricula));
      if (plan.sesiones !== undefined) sheet.cell(`M${row}`).value(plan.sesiones ? 'SI' : 'NO');
      if (plan.nSesiones !== undefined) sheet.cell(`N${row}`).value(plan.nSesiones);
      if (plan.sesionesUtilizadas !== undefined) sheet.cell(`O${row}`).value(plan.sesionesUtilizadas);
      if (plan.permiteRenovacion !== undefined) sheet.cell(`P${row}`).value(plan.permiteRenovacion ? 'SI' : 'NO');
      
      // Configuración adicional (columnas Q-X)
      if (plan.expiracionRenovacion !== undefined) sheet.cell(`Q${row}`).value(plan.expiracionRenovacion);
      if (plan.codigoTurno !== undefined) sheet.cell(`R${row}`).value(plan.codigoTurno);
      if (plan.factura !== undefined) sheet.cell(`S${row}`).value(plan.factura ? 'SI' : 'NO');
      if (plan.congelar !== undefined) sheet.cell(`T${row}`).value(plan.congelar);
      if (plan.mesCalendario !== undefined) sheet.cell(`U${row}`).value(plan.mesCalendario ? 'SI' : 'NO');
      if (plan.idFamilia !== undefined) sheet.cell(`V${row}`).value(plan.idFamilia);
      if (plan.idSubFamilia !== undefined) sheet.cell(`W${row}`).value(plan.idSubFamilia);
      if (plan.idPlan !== undefined) sheet.cell(`X${row}`).value(plan.idPlan);
      
      // Clasificación y tipo (columnas Y-AD)
      if (plan.modalidad) sheet.cell(`Y${row}`).value(plan.modalidad);
      if (plan.tipoDePlan) sheet.cell(`Z${row}`).value(plan.tipoDePlan);
      if (plan.tipoPlanPagado) sheet.cell(`AA${row}`).value(plan.tipoPlanPagado);
      if (plan.subFamiliaPlan) sheet.cell(`AB${row}`).value(plan.subFamiliaPlan);
      if (plan.dcto) sheet.cell(`AC${row}`).value(plan.dcto);
      if (plan.tipoIva) sheet.cell(`AD${row}`).value(plan.tipoIva);
      
      // Fechas importantes (columnas AE-AK)
      if (plan.fechaDeIngreso) sheet.cell(`AE${row}`).value(plan.fechaDeIngreso);
      if (plan.fechaDeInicio) sheet.cell(`AF${row}`).value(plan.fechaDeInicio);
      if (plan.fechaDeVencimiento) sheet.cell(`AG${row}`).value(plan.fechaDeVencimiento);
      if (plan.fechaDeVigencia) sheet.cell(`AH${row}`).value(plan.fechaDeVigencia);
      if (plan.fechaUltimoPago) sheet.cell(`AI${row}`).value(plan.fechaUltimoPago);
      if (plan.horaUltimoPago) sheet.cell(`AJ${row}`).value(plan.horaUltimoPago);
      if (plan.fechaPagoMatricula) sheet.cell(`AK${row}`).value(plan.fechaPagoMatricula);
      
      // Información adicional (columnas AL-AT)
      if (plan.unidadExpiracionRenovacion) sheet.cell(`AL${row}`).value(plan.unidadExpiracionRenovacion);
      if (plan.turno) sheet.cell(`AM${row}`).value(plan.turno);
      if (plan.sucursalUltimoPago) sheet.cell(`AN${row}`).value(plan.sucursalUltimoPago);
      if (plan.sucursalUltimoAcceso) sheet.cell(`AO${row}`).value(plan.sucursalUltimoAcceso);
      if (plan.fechaHoraUltimoAcceso) sheet.cell(`AP${row}`).value(plan.fechaHoraUltimoAcceso);
      if (plan.nombreSucursalAsignacion) sheet.cell(`AQ${row}`).value(plan.nombreSucursalAsignacion);
      if (plan.nombreUsuarioAsignacion) sheet.cell(`AR${row}`).value(plan.nombreUsuarioAsignacion);
      if (plan.estadoProcesoPago) sheet.cell(`AS${row}`).value(plan.estadoProcesoPago);
    },

    fillDTESheet(sheet, record, tipoDTE, documentData, row) {
      // 📋 HOJA 6: DTE - Documentos Tributarios Electrónicos
      
      // ID Transacción (columna C) - para relacionar con otras hojas
      if (record.idTransaccion) sheet.cell(`C${row}`).value(record.idTransaccion);
      
      // Tipo de DTE (columna D)
      sheet.cell(`D${row}`).value(tipoDTE);
      
      // Tipo de Documento completo (columna E)
      if (documentData.TIPO_DOCUMENTO) sheet.cell(`E${row}`).value(documentData.TIPO_DOCUMENTO);
      
      // ID Documento (columna F) - Manejo especial para notas de crédito
      if (tipoDTE === 'NOTA_CREDITO' && documentData.CREDIT_NOTE_ID) {
        sheet.cell(`F${row}`).value(documentData.CREDIT_NOTE_ID);
      } else if (documentData.ID_DOCUMENTO) {
        sheet.cell(`F${row}`).value(documentData.ID_DOCUMENTO);
      }
      
      // Número del documento (columna G) - Manejo especial para notas de crédito
      if (tipoDTE === 'NOTA_CREDITO' && documentData.CREDIT_NOTE_NUMBER) {
        sheet.cell(`G${row}`).value(documentData.CREDIT_NOTE_NUMBER);
      } else if (documentData.NUMBER) {
        sheet.cell(`G${row}`).value(documentData.NUMBER);
      }
      
      // Fechas (columnas H, I, J) - Conversión de UTC a DD-MM-YYYY
      if (documentData.EMISSION_DATE_UTC) {
        const fechaEmision = this.convertUTCToDate(documentData.EMISSION_DATE_UTC);
        sheet.cell(`H${row}`).value(fechaEmision);
      }
      if (documentData.EXPIRATION_DATE_UTC) {
        const fechaVencimiento = this.convertUTCToDate(documentData.EXPIRATION_DATE_UTC);
        sheet.cell(`I${row}`).value(fechaVencimiento);
      }
      if (documentData.GENERATION_DATE_UTC) {
        const fechaGeneracion = this.convertUTCToDate(documentData.GENERATION_DATE_UTC);
        sheet.cell(`J${row}`).value(fechaGeneracion);
      }
      
      // Montos (columnas K, L, M, N)
      if (tipoDTE === 'NOTA_CREDITO') {
        // Para notas de crédito, usar AMOUNT
        if (documentData.AMOUNT !== undefined) {
          sheet.cell(`K${row}`).value(parseFloat(documentData.AMOUNT));
          // Las notas de crédito no tienen desglose de neto/IVA/exento
          sheet.cell(`L${row}`).value('');
          sheet.cell(`M${row}`).value('');
          sheet.cell(`N${row}`).value('');
        }
      } else {
        // Para facturas y boletas
        if (documentData.TOTAL_AMOUNT !== undefined) sheet.cell(`K${row}`).value(parseFloat(documentData.TOTAL_AMOUNT));
        if (documentData.NET_AMOUNT !== undefined) sheet.cell(`L${row}`).value(parseFloat(documentData.NET_AMOUNT));
        if (documentData.TAX_AMOUNT !== undefined) sheet.cell(`M${row}`).value(parseFloat(documentData.TAX_AMOUNT));
        if (documentData.EXEMPT_AMOUNT !== undefined) sheet.cell(`N${row}`).value(parseFloat(documentData.EXEMPT_AMOUNT));
      }
      
      // Motivo (columna O) - solo para notas de crédito
      if (tipoDTE === 'NOTA_CREDITO' && documentData.MOTIVE) {
        sheet.cell(`O${row}`).value(documentData.MOTIVE);
      } else {
        sheet.cell(`O${row}`).value('');
      }
      
      // Información adicional de la venta (columnas P, Q, R)
      if (record.estadoVenta) sheet.cell(`P${row}`).value(record.estadoVenta);
      if (record.rutCliente) sheet.cell(`Q${row}`).value(record.rutCliente);
      if (record.nombreCliente) sheet.cell(`R${row}`).value(record.nombreCliente);
      
      // URL del documento PDF (columna S) - NUEVO
      if (tipoDTE === 'NOTA_CREDITO' && documentData.CREDIT_NOTE_URL_PDF) {
        sheet.cell(`S${row}`).value(documentData.CREDIT_NOTE_URL_PDF);
      } else if (documentData.URL_PDF) {
        sheet.cell(`S${row}`).value(documentData.URL_PDF);
      } else {
        sheet.cell(`S${row}`).value('');
      }
    },
    
    // Método auxiliar para convertir timestamp UTC a formato DD-MM-YYYY
    convertUTCToDate(utcTimestamp) {
      if (!utcTimestamp) return '';
      
      // Si ya es una cadena de fecha, devolverla tal cual
      if (typeof utcTimestamp === 'string' && utcTimestamp.includes('-')) {
        return utcTimestamp;
      }
      
      // Convertir timestamp a fecha
      const date = new Date(utcTimestamp * 1000); // Multiplicar por 1000 si viene en segundos
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      
      return `${day}-${month}-${year}`;
    },

    async fetchExcelTemplate(templateName) {
      try {
        const response = await this.$XLSXTemplateDownload.post(
          "",
          {
            template: templateName,
          },
          {
            headers: {
              "Content-Type": "application/json",
              token: sessionStorage.getItem("jCxbraoQeD"),
            },
            responseType: "arraybuffer",
          }
        );
        return response.data;
      } catch (error) {
        throw error;
      }
    },

    async fetchCatalogs() {
      try {
        this.$logger.log('📋 Obteniendo catálogos de ventas del gimnasio...');
        
        // ✅ NUEVO FORMATO v2.4: Body con Header.Operacion y Auth.Token
        const response = await this.$CentralGymOPS_API.post(
          "",
          {
            Header: {
              Operacion: "/v1/sales/get/gymsalesviewcatalogs"
            },
            Auth: {
              Token: sessionStorage.getItem("jCxbraoQeD")
            },
            Parameters: {},
            Body: {}
          },
          {
            headers: {
              "Content-Type": "application/json"
            }
          }
        );

        if (response.data && response.data.result) {
          const catalogs = response.data.result;
          
          // Mapear nuevos catálogos v2.3
          this.branches = catalogs.sucursales || [];                    // Array de nombres de sucursales
          this.paymentMethodsDictionary = catalogs.estadosMediosPago || []; // Métodos de pago
          this.saleStatusDictionary = catalogs.estadosVenta || [];      // Estados de venta ([CERRADA], [ABIERTA])
          this.closeStatusDictionary = catalogs.estadosCierreVenta || []; // Estados de cierre ([VIGENTE], [ANULADA])
          
          // Mantener compatibilidad (usar estados de cierre como estados principales)
          this.statusDictionary = catalogs.estadosCierreVenta || [];
          
          this.$logger.log('📋 Catálogos cargados exitosamente:', {
            sucursales: this.branches.length,
            estadosMediosPago: this.paymentMethodsDictionary.length,
            estadosVenta: this.saleStatusDictionary.length,
            estadosCierreVenta: this.closeStatusDictionary.length
          });
          
        } else {
          throw new Error("No se recibieron los catálogos del servidor");
        }
      } catch (error) {
        // Aseguramos que el modal de carga se cierre
        this.$swal.close();

        let errorMessage = "Error al cargar los catálogos";
        
        if (error.response) {
          switch (error.response.status) {
            case 401:
              errorMessage = "No tienes autorización para acceder a estos datos";
              break;
            case 403:
              errorMessage = "No tienes permisos suficientes para acceder a estos datos";
              break;
            case 500:
              errorMessage = "Error interno del servidor";
              break;
            default:
              errorMessage = error.response.data?.message || 
                            "Error en la comunicación con el servidor";
          }
        } else if (error.request) {
          errorMessage = "No se pudo establecer conexión con el servidor";
        }

        throw new Error(errorMessage);
      }
    },

    async handleSearch(filters) {
      // Usar todos los filtros (básicos y avanzados) tal como vienen
      const searchParams = { ...filters };
      await this.handleSearchUnified(searchParams);
    },

    async handleFetchNewPage(filters, page) {
      // Paginación por servidor: traer página solicitada
      try {
        this.loading = true;
        const Parameters = this.mapFiltersToParameters(filters);
        Parameters.page = page;
        Parameters.limit = this.localPagination.itemsPerPage || 20;

        const response = await this.$CentralGymOPS_API.post(
          "",
          {
            Header: { Operacion: "/v1/sales/get/gymsales" },
            Auth: { Token: sessionStorage.getItem("jCxbraoQeD") },
            Parameters,
            Body: {}
          },
          { headers: { "Content-Type": "application/json" } }
        );

        const result = response.data?.result || {};
        this.records = Array.isArray(result.sales) ? result.sales : [];
        // Total para paginación
        if (typeof result.totalRecords === 'number') this.totalRecords = result.totalRecords;
        else if (typeof result.total === 'number') this.totalRecords = result.total;
        else this.totalRecords = (page + 1) * (result.limit || this.localPagination.itemsPerPage || 20); // fallback aproximado

        // Sin tocar métricas (persisten para el rango/criterios actuales)
      } catch (error) {
        this.$logger.error('❌ Error al obtener página:', error);
        this.$swal.fire({ icon: 'error', title: 'Error en paginación', text: 'No fue posible cargar la página solicitada' });
      } finally {
        this.loading = false;
      }
    },

    async fetchRecords(filters, page) {
      // ACTUALIZADO v2.3: Usar misma lógica que handleSearchUnified
      try {
        this.$logger.log('📄 Obteniendo página de registros:', page, 'con filtros:', filters);
        
        // ACTUALIZAR: Usar nuevos parámetros en español v2.3
        let Parameters = {
          page: page,
          limit: 20  // Cambiar pageSize por limit
        };
        
        // ========================================
        // USAR MISMA LÓGICA QUE handleSearchUnified
        // ========================================
        
        // nombreSucursal - Filtro por nombre de sucursal
        if (filters.branch) {
          const branchName = this.getBranchNameById(filters.branch);
          if (branchName) {
            Parameters.nombreSucursal = branchName;
          }
        }
        
        // estadoVenta - Estados de venta CON MAPEO ESPECIAL v2.3
        if (filters.status) {
          if (filters.status === '[VIGENTE]') {
            Parameters.estadoVenta = ""; // VIGENTE = cadena vacía
          } else if (filters.status === '[ANULADA]') {
            Parameters.estadoVenta = "[ANULADA]"; // ANULADA = tal como viene
          } else {
            Parameters.estadoVenta = filters.status;
          }
        }
        
        // metodoPago - Método de pago (si existe en filtros)
        if (filters.paymentMethod) {
          Parameters.metodoPago = filters.paymentMethod;
        }
        
        // Filtros de fecha
        if (filters.dateFrom) Parameters.fechaDesde = this.formatDateForAPI(filters.dateFrom);
        if (filters.dateTo) Parameters.fechaHasta = this.formatDateForAPI(filters.dateTo);
        
        // nombreVendedor - Vendedor
        if (filters.seller) {
          Parameters.nombreVendedor = filters.seller;
        }
        if (filters.sellerName) {
          Parameters.nombreVendedor = filters.sellerName;
        }
        // cliente
        if (filters.clientName) {
          Parameters.nombreCliente = filters.clientName;
        }
        // caja/POS
        if (filters.cashier) {
          Parameters.cajaPuntoDeVenta = filters.cashier;
        }
        // tipo de plan
        if (filters.planType) {
          Parameters.planType = filters.planType;
        }
        // montos
        if (filters.amountFrom) {
          Parameters.amountFrom = filters.amountFrom;
        }
        if (filters.amountTo) {
          Parameters.amountTo = filters.amountTo;
        }
        
        // Búsqueda inteligente por texto
        if (filters.searchText && filters.searchText.trim()) {
          const searchText = filters.searchText.trim();
          if (!isNaN(searchText) && searchText.length > 0) {
            Parameters.idTransaccion = searchText;
          } else if (searchText.includes('-') && searchText.length >= 9) {
            Parameters.rutCliente = searchText;
          } else {
            Parameters.nombreCliente = searchText;
          }
        }
        
        // ✅ NUEVO FORMATO v2.4: Body con Header.Operacion y Auth.Token
        const response = await this.$CentralGymOPS_API.post(
          "",
          {
            Header: {
              Operacion: "/v1/sales/get/gymsales" // ENDPOINT REAL
            },
            Auth: {
              Token: sessionStorage.getItem("jCxbraoQeD")
            },
            Parameters: Parameters,
            Body: {}
          },
          {
            headers: {
              "Content-Type": "application/json"
            }
          }
        );

        if (response.data && response.data.result && response.data.result.sales) {
          // Agregar los nuevos registros al array existente
          this.records.push(...response.data.result.sales);
          
          // Si recibimos menos registros que el tamaño de página, no hay más para cargar
          if (response.data.result.sales.length < 20) {
            if (this.$refs.gymSalesTable) {
              this.$refs.gymSalesTable.stopFetching();
            }
          }
        } else {
          throw new Error("No se recibieron datos del servidor");
        }
      } catch (error) {
        // Primero cerramos el modal de carga
        this.$swal.close();

        let errorMessage = "Ha ocurrido un error al obtener los registros";

        if (error.response) {
          switch (error.response.status) {
            case 401:
              errorMessage = "No tienes autorización para acceder a estos datos. Por favor, inicia sesión nuevamente.";
              break;
            case 403:
              errorMessage = "No tienes permisos suficientes para acceder a estos datos.";
              break;
            case 404:
              errorMessage = "No se encontró el recurso solicitado en el servidor.";
              break;
            case 500:
              errorMessage = "Error interno del servidor. Por favor, intenta más tarde.";
              break;
            default:
              errorMessage = error.response.data?.message || 
                            "Error en la comunicación con el servidor";
          }
        } else if (error.request) {
          errorMessage = "No se pudo establecer conexión con el servidor. Por favor, verifica tu conexión a internet.";
        }

        await this.$swal.fire({
          icon: 'error',
          title: '¡Error al cargar los datos!',
          html: `
            <div class="text-left">
              <p><strong>Detalles del error:</strong></p>
              <p>${errorMessage}</p>
              <p class="text-muted small">Si el problema persiste, contacta a soporte técnico.</p>
            </div>
          `,
          confirmButtonText: 'Entendido',
          confirmButtonColor: '#6bd098'
        });

        if (error.response?.status === 401) {
          sessionStorage.clear();
          this.$router.push('/login');
          return;
        }

        throw error;
      }
    },

    // NUEVO: Método para obtener todos los registros filtrados (similar a OnLineSalesDashboard)
    // Eliminado: fetchAllFilteredRecordsForSearch (ya no descargamos todo el dataset)
    
    // NUEVO: Método para carga inicial ultra-rápida (ÚLTIMAS 20 VENTAS)
    async fetchFirstPageOnly() {
      try {
        this.$logger.log('⚡ Carga inicial ultra-rápida: Obteniendo las últimas 20 ventas...');
        const response = await this.$CentralGymOPS_API.post(
          "",
          {
            Header: { Operacion: "/v1/sales/get/gymsales" },
            Auth: { Token: sessionStorage.getItem("jCxbraoQeD") },
            Parameters: { page: 0, limit: 20 },
            Body: {}
          },
          { headers: { "Content-Type": "application/json" } }
        );

        const result = response.data?.result || {};
        this.records = Array.isArray(result.sales) ? result.sales : [];
        // Establecer total para UI (si viene)
        if (typeof result.totalRecords === 'number') this.totalRecords = result.totalRecords;
        else if (typeof result.total === 'number') this.totalRecords = result.total;
        else this.totalRecords = this.records.length;

        // Configurar paginación visible
        this.localPagination.itemsPerPage = 20;
        this.localPagination.currentPage = 1;
        this.localPagination.totalPages = Math.max(1, Math.ceil(this.totalRecords / this.localPagination.itemsPerPage));

      } catch (error) {
        this.$logger.error('❌ Error en carga inicial de las últimas ventas:', error);
        throw error;
      }
    },
    
    // NUEVO: Método para calcular KPIs desde los registros de ventas
    calculateKPIsFromRecords(records) {
      if (!records || records.length === 0) {
        this.totalFilteredRecords = 0;
        this.totalSalesAmount = 0;
        this.totalValidSales = 0;
        this.totalCancelledSales = 0;
        this.totalClosedSales = 0;
        return;
      }
      
      this.totalFilteredRecords = records.length;
      
      // Calcular monto total de ventas
      this.totalSalesAmount = records.reduce((sum, sale) => {
        const amount = this.parseMonetaryValue(sale.ventaTotal) || 0;
        return sum + amount;
      }, 0);
      
      // ACTUALIZADO v2.3: Contar ventas válidas (vigentes = estadoVenta vacío)
      this.totalValidSales = records.filter(sale => 
        sale.estadoVenta === '' || sale.estadoVenta === null || sale.estadoVenta === undefined
      ).length;
      
      // ACTUALIZADO v2.3: Contar ventas anuladas (estadoVenta = "[ANULADA]")
      this.totalCancelledSales = records.filter(sale => 
        sale.estadoVenta === '[ANULADA]'
      ).length;
      
      // Contar ventas cerradas
      this.totalClosedSales = records.filter(sale => 
        sale.estadoCierre === '[CERRADA]'
      ).length;
    },
    
    // NUEVO: Método unificado de búsqueda (similar a OnLineSalesDashboard)
    async handleSearchUnified(searchParams) {
      try {
        this.loading = true;
        // Notificar al hijo para apagar el indicador local
        if (this.$refs.gymSalesTable) {
          this.$refs.gymSalesTable.isApplyingAdvancedFilters = false;
        }
        // Limpiar y preparar
        this.records = [];

        // Mapear filtros a Parameters y traer SOLO primera página
        const Parameters = this.mapFiltersToParameters(searchParams);
        const page = 0;
        const limit = 20;

        // Guardar filtros para navegación entre páginas
        this.lastSearchFilters = { ...searchParams };

        const metricsParameters = this.mapFiltersToMetricsParameters(searchParams);
        const [recordsResp, metricsResult] = await Promise.all([
          this.$CentralGymOPS_API.post(
            "",
            { Header: { Operacion: "/v1/sales/get/gymsales" }, Auth: { Token: sessionStorage.getItem("jCxbraoQeD") }, Parameters: { ...Parameters, page, limit }, Body: {} },
            { headers: { "Content-Type": "application/json" } }
          ),
          this.fetchMetricsForFilters(metricsParameters)
        ]);

        const result = recordsResp.data?.result || {};
        this.records = Array.isArray(result.sales) ? result.sales : [];
        if (typeof result.totalRecords === 'number') this.totalRecords = result.totalRecords;
        else if (typeof result.total === 'number') this.totalRecords = result.total;
        else this.totalRecords = this.records.length;

        // Configurar paginación visible basada en servidor
        this.localPagination.itemsPerPage = limit;
        this.localPagination.currentPage = 1;
        this.localPagination.totalPages = Math.max(1, Math.ceil(this.totalRecords / limit));

        // Pasar métricas al hijo para renderizar gráficos/KPIs
        if (this.$refs.gymSalesTable && typeof this.$refs.gymSalesTable.applyMetricsFromAPI === 'function') {
          this.$refs.gymSalesTable.applyMetricsFromAPI(metricsResult);
        }
        
      } catch (error) {
        this.$logger.error('Error en búsqueda unificada de ventas:', error);
        this.$swal.fire({
          icon: "error",
          title: "Error en la búsqueda",
          text: `${error.message || error}. Vuelve a intentarlo más tarde.`,
          confirmButtonColor: "#6bd098",
        });
      } finally {
        this.loading = false;
        // Asegurar que el banner "Aplicando filtros" se cierre
        if (this.$refs.gymSalesTable) {
          this.$refs.gymSalesTable.isApplyingAdvancedFilters = false;
        }
      }
    },
    
    // ========================================
    // MÉTODOS AUXILIARES PARA NUEVA API v2.3
    // ========================================
    
    getBranchNameById(branchId) {
      // ACTUALIZADO v2.3: branches ahora es array de nombres directos
      if (!branchId) return null;
      
      // Verificar si branchId es un nombre que existe en el catálogo
      if (this.branches.includes(branchId)) {
        this.$logger.log('🏢 Sucursal encontrada en catálogo:', branchId);
        return branchId;
      }
      
      // Si no está en el catálogo, intentar buscar por similitud parcial
      const foundBranch = this.branches.find(branch => 
        branch.toLowerCase().includes(branchId.toLowerCase()) ||
        branchId.toLowerCase().includes(branch.toLowerCase())
      );
      
      if (foundBranch) {
        this.$logger.log('🏢 Sucursal encontrada por similitud:', { 
          buscado: branchId, 
          encontrado: foundBranch 
        });
        return foundBranch;
      }
      
      // Si no se encuentra, usar tal como viene
      this.$logger.log('🏢 Sucursal no encontrada en catálogo, usando tal como viene:', branchId);
      return branchId;
    },
    
    formatDateForAPI(dateString) {
      // Convertir fecha de formato YYYY-MM-DD a DD-MM-YYYY para la API
      if (!dateString) return null;
      
      try {
        // ✅ CORREGIDO: Evitar problemas de zona horaria usando split en lugar de new Date()
        // Cuando se hace new Date("YYYY-MM-DD"), JavaScript lo interpreta como UTC
        // pero getDate(), getMonth() usan zona horaria local, causando offset de días
        
        // Dividir la fecha YYYY-MM-DD manualmente para evitar problemas de zona horaria
        const dateParts = dateString.split('-');
        
        if (dateParts.length !== 3) {
          this.$logger.warn('⚠️ Formato de fecha inválido (esperado YYYY-MM-DD):', dateString);
          return dateString;
        }
        
        const year = dateParts[0];
        const month = dateParts[1];  // Ya viene con padding de 2 dígitos
        const day = dateParts[2];    // Ya viene con padding de 2 dígitos
        
        // Validar que sean números válidos
        if (isNaN(parseInt(year)) || isNaN(parseInt(month)) || isNaN(parseInt(day))) {
          this.$logger.warn('⚠️ Fecha con valores no numéricos:', dateString);
          return dateString;
        }
        
        // Validar rangos básicos
        const monthNum = parseInt(month);
        const dayNum = parseInt(day);
        
        if (monthNum < 1 || monthNum > 12) {
          this.$logger.warn('⚠️ Mes inválido:', month);
          return dateString;
        }
        
        if (dayNum < 1 || dayNum > 31) {
          this.$logger.warn('⚠️ Día inválido:', day);
          return dateString;
        }
        
        // Formatear a DD-MM-YYYY
        const formattedDate = `${day}-${month}-${year}`;
        this.$logger.log('📅 Fecha formateada para API (sin problemas de zona horaria):', { 
          original: dateString, 
          formatted: formattedDate,
          metodo: 'split() en lugar de new Date()'
        });
        
        return formattedDate;
        
      } catch (error) {
        this.$logger.error('❌ Error al formatear fecha:', error);
        return dateString; // Retornar sin cambios en caso de error
      }
    },
    
    getSellerNameById(sellerId) {
      // Método para obtener nombre de vendedor por ID (si se implementa catálogo de vendedores)
      // Por ahora retornar el ID tal como viene
      if (!sellerId) return null;
      
      // TODO: Implementar búsqueda en catálogo de vendedores si se agrega
      this.$logger.log('👨‍💼 Usando ID de vendedor como nombre:', sellerId);
      return sellerId;
    },
    
    // ========================================
    // MÉTODO NUEVO v2.3: MANEJO DE BÚSQUEDA DESDE COMPONENTE TABLA
    // ========================================
    
    async handleSearchFromTable(searchParams) {
      try {
        this.$logger.log('🔄 Búsqueda desde tabla (GymSales):', searchParams);
        
        // Si es un cambio de página, solicitar datos al servidor
        if (searchParams.isLocalPageChange) {
          if (searchParams.itemsPerPage && searchParams.itemsPerPage !== this.localPagination.itemsPerPage) {
            this.localPagination.itemsPerPage = searchParams.itemsPerPage;
          }
          const pageToFetch = searchParams.page; // base 0
          await this.handleFetchNewPage(this.lastSearchFilters || {}, pageToFetch);
          // Actualizar paginador visible
          this.localPagination.currentPage = pageToFetch + 1;
          return;
        }
        
        // Si es una nueva búsqueda, usar el método unificado existente
        const unifiedSearchParams = { ...searchParams };
        
        await this.handleSearchUnified(unifiedSearchParams);
        
      } catch (error) {
        this.$logger.error('❌ Error en búsqueda desde tabla (GymSales):', error);
      }
    },
    
    // ========================================
    // MÉTODO NUEVO v2.3: CONFIGURACIÓN DE PAGINACIÓN LOCAL
    // ========================================
    
    setupLocalPagination(totalRecords, itemsPerPage = 20) {
      // Configurar paginación local basándose en el total de registros filtrados
      this.localPagination.itemsPerPage = itemsPerPage;
      this.localPagination.totalPages = Math.ceil(totalRecords / itemsPerPage);
      this.localPagination.currentPage = 1; // Siempre empezar en página 1
      
      // Actualizar totalRecords para que el componente tabla maneje la paginación correctamente
      this.totalRecords = totalRecords;
      
      this.$logger.log('📄 Paginación local configurada:', {
        totalRegistros: totalRecords,
        itemsPorPagina: itemsPerPage,
        totalPaginas: this.localPagination.totalPages,
        paginaActual: this.localPagination.currentPage
      });
    },
    
    // ========================================
    // MÉTODO NUEVO v2.3: ACTUALIZACIÓN DE PAGINACIÓN LOCAL
    // ========================================
    
    updateLocalPagination(page, itemsPerPage = null) {
      // Validar que tenemos datos para paginar
      if (!this.allFilteredRecords || this.allFilteredRecords.length === 0) {
        this.records = [];
        this.localPagination.currentPage = 1;
        this.totalRecords = 0;
        this.$logger.log('📄 Paginación: Sin datos para mostrar');
        return;
      }
      
      // Actualizar configuración si cambia items per page
      if (itemsPerPage && itemsPerPage !== this.localPagination.itemsPerPage) {
        this.localPagination.itemsPerPage = itemsPerPage;
        this.localPagination.totalPages = Math.ceil(this.allFilteredRecords.length / itemsPerPage);
      }
      
      // Validar página
      if (page < 1) page = 1;
      if (page > this.localPagination.totalPages) page = this.localPagination.totalPages;
      
      this.localPagination.currentPage = page;
      
      // Calcular slice de datos para la página actual
      const startIndex = (page - 1) * this.localPagination.itemsPerPage;
      const endIndex = startIndex + this.localPagination.itemsPerPage;
      
      // CORREGIDO v2.3: Actualizar records con SLICE LOCAL (solo página actual)
      this.records = this.allFilteredRecords.slice(startIndex, endIndex);
      
      // Actualizar totalRecords para que la paginación funcione correctamente
      this.totalRecords = this.allFilteredRecords.length;
      
      this.$logger.log('📄 Paginación local actualizada correctamente:', {
        paginaActual: page,
        registrosMostradosEnPagina: this.records.length,
        rangoIndices: `${startIndex} - ${Math.min(endIndex - 1, this.allFilteredRecords.length - 1)}`,
        totalRegistrosFiltrados: this.allFilteredRecords.length,
        totalPaginas: this.localPagination.totalPages
      });
    },
    
    // ========================================
    // MÉTODO ACTUALIZADO: VALIDACIÓN DE FECHAS
    // ========================================
    
    validateDateRange(dateFrom, dateTo) {
      if (!dateFrom || !dateTo) return { valid: true }; // Sin fechas = válido
      
      const fromDate = new Date(dateFrom);
      const toDate = new Date(dateTo);
      
      // Validar que las fechas sean válidas
      if (isNaN(fromDate.getTime()) || isNaN(toDate.getTime())) {
        return {
          valid: false,
          message: 'Las fechas ingresadas no son válidas. Use el formato correcto.'
        };
      }
      
      // Validar que la fecha desde no sea mayor que la fecha hasta
      if (fromDate > toDate) {
        return {
          valid: false,
          message: 'La fecha "desde" no puede ser mayor que la fecha "hasta".'
        };
      }
      
      const diffTime = Math.abs(toDate - fromDate);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      const maxDays = 365; // 1 año máximo
      
      if (diffDays > maxDays) {
        return {
          valid: false,
          message: `El rango de fechas no puede ser mayor a 1 año. Rango actual: ${diffDays} días.`
        };
      }
      
      return { valid: true };
    },
    
    // ========================================
    // MÉTODOS PARA BÚSQUEDA AUTOMÁTICA DEL MES EN CURSO
    // ========================================
    
    // Obtener el primer día del mes actual en formato YYYY-MM-DD
    getFirstDayOfCurrentMonth() {
      const today = new Date();
      const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
      return firstDay.toISOString().split('T')[0];
    },
    
    // Obtener el último día del mes actual en formato YYYY-MM-DD
    getLastDayOfCurrentMonth() {
      const today = new Date();
      const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      return lastDay.toISOString().split('T')[0];
    },
    
    // Realizar búsqueda automática del mes en curso
    async performCurrentMonthSearch() {
      try {
        this.$logger.log('📅 Iniciando búsqueda automática del mes en curso...');
        
        const currentMonthFilters = {
          dateFrom: this.getFirstDayOfCurrentMonth(),
          dateTo: this.getLastDayOfCurrentMonth(),
          status: '', // Todos los estados
          paymentMethod: '', // Todos los métodos
          branch: '', // Todas las sucursales
          searchText: '' // Sin búsqueda de texto
        };
        
        await this.handleSearchUnified(currentMonthFilters);
        
        return true;
      } catch (error) {
        this.$logger.error('❌ Error en búsqueda automática del mes en curso:', error);
        throw error;
      }
    },
  },
  beforeMount() {
    this.$swal.fire({
      title: "Cargando!",
      text: "Espera un momento...",
      showConfirmButton: false,
      allowOutsideClick: false,
      didOpen: () => {
        this.$swal.showLoading();
      },
    });

    // ACTUALIZADO v2.3: Los branches ahora se obtienen del catálogo, no del sessionStorage
    // this.branches se cargará automáticamente en fetchCatalogs()
    
    Promise.all([this.fetchCatalogs()])
      .then(() => {
        // NUEVO: Búsqueda automática del mes en curso al cargar el componente
        this.$logger.log('📅 Inicializando ventas de gimnasio con búsqueda automática del mes en curso...');
        
        return this.performCurrentMonthSearch().then(() => {
          this.$swal.close();
          
          const mesActual = new Date().toLocaleString('es-CL', { month: 'long', year: 'numeric' });
          
          this.$logger.log('✅ Inicialización de ventas del mes en curso completada exitosamente:', {
            ventasDelMes: this.allFilteredRecords ? this.allFilteredRecords.length : 0,
            mes: mesActual,
            montoTotal: this.totalSalesAmount,
            mensaje: '✅ Mostrando ventas del mes actual automáticamente'
          });
          
          // Modal informativo deshabilitado por solicitud del usuario
          // Los datos del mes actual se cargan automáticamente sin mostrar resumen
          
        }).catch((error) => {
          this.$logger.error('❌ Error en búsqueda automática del mes en curso:', error);
          this.$swal.fire({
            icon: 'error',
            title: '❌ Error en Búsqueda Automática',
            html: `
              <div class="text-left">
                <p><strong>No se pudieron cargar las ventas del mes en curso</strong></p>
                <p class="text-muted">${error.message || 'Error de conexión'}</p>
                <hr>
                <p><strong>💡 Opciones disponibles:</strong></p>
                <ul>
                  <li>🔄 Recargue la página para intentar nuevamente</li>
                  <li>🔍 Use los filtros para realizar búsquedas específicas</li>
                  <li>📅 Configure manualmente el rango de fechas</li>
                  <li>📞 Contacte a soporte si el problema persiste</li>
                </ul>
              </div>
            `,
            confirmButtonColor: '#6bd098',
            confirmButtonText: 'Entendido'
          });
        });
      })
      .catch(async (error) => {
        // Primero cerramos el modal de carga
        this.$swal.close();
        
        // Mostramos el modal de error y esperamos a que el usuario lo cierre
        await this.$swal.fire({
          icon: "error",
          title: "Error",
          html: `
            <div class="text-left">
              <p><strong>Detalles del error:</strong></p>
              <p>${error.message || "Ha ocurrido un error inesperado"}</p>
              <p class="text-muted small">Si el problema persiste, contacta a soporte técnico.</p>
            </div>
          `,
          showConfirmButton: true,
          confirmButtonText: 'Entendido',
          confirmButtonColor: "#6bd098",
          showClass: {
            popup: 'animate__animated animate__fadeInDown'
          },
          hideClass: {
            popup: 'animate__animated animate__fadeOutUp'
          }
        });
      });
  },
};

/*
  RESUMEN EJECUTIVO - GymSales v2.5 UX MEJORADA + SISTEMA MULTI-HOJA + CATÁLOGOS REALES + EXPORTACIÓN DEFINITIVA
  =======================================================================================
  
  🆕 CAMBIOS v2.5 - MEJORA DE UX EN FILTROS BÁSICOS:
  - ✅ ETIQUETAS DESCRIPTIVAS: Agregadas a todos los filtros básicos en GymSalesTable (Búsqueda, Sucursal, Estado, Método)
  - ✅ UX MÓVIL MEJORADA: Etiquetas con iconos y estilos específicos para móvil (text-white fw-bold)
  - ✅ ACCESIBILIDAD: Labels con atributos 'for' asociados a inputs/selects específicos
  - ✅ IDENTIFICACIÓN VISUAL: Iconos distintivos para cada tipo de filtro (search, building, check-circle, credit-card)
  - ✅ CONSISTENCIA: Mismo diseño aplicado tanto en desktop como móvil
  - ✅ RESPONSIVE: Estilos específicos y adaptativos para diferentes tamaños de pantalla
  - ✅ PROBLEMA RESUELTO: En vista móvil no se entendía qué función tenía cada filtro básico
  
  🆕 CAMBIOS v2.4 - SISTEMA MULTI-HOJA IMPLEMENTADO:
  - ✅ 5 HOJAS ESPECIALIZADAS: CONSOLIDADO, VENTAS, CONTROL Y DETALLE, PRODUCTOS, MEMBRESIAS
  - ✅ CONSOLIDADO: Vista ejecutiva con concatenación (compatible con versión anterior)
  - ✅ VENTAS: Información básica de transacciones (1 fila por venta)
  - ✅ CONTROL: Formas de pago detalladas (1 fila por forma de pago)
  - ✅ PRODUCTOS: Análisis de inventario (1 fila por producto vendido)
  - ✅ MEMBRESIAS: Análisis de planes (1 fila por plan/membresía)
  - ✅ RELACIÓN PERFECTA: ID Transacción en columna C de cada hoja para correlacionar
  - ✅ ANÁLISIS GRANULAR: Sin pérdida de datos, cada elemento múltiple tiene su fila
  
  🔧 CAMBIOS v2.3 PREVIOS - CATÁLOGOS REALES:
  - ✅ Operación /v1/sales/get/gymsalesviewcatalogs implementada
  - ✅ sucursales: Array de nombres de sucursales reales
  - ✅ estadosMediosPago: Métodos de pago ([EFECTIVO], [TARJETA DE DÉBITO], etc.)
  - ✅ estadosVenta: Estados de venta ([CERRADA], [ABIERTA])
  - ✅ estadosCierreVenta: Estados de cierre ([VIGENTE], [ANULADA])
  - ✅ Filtros básicos conectados con operación /v1/sales/get/gymsales
  - ✅ Filtros avanzados trabajando en memoria
  - ✅ PAGINACIÓN LOCAL CORREGIDA: Respeta 20 registros por página
  
  🎨 NUEVOS CAMBIOS v2.3 - LÓGICA DE ESTADOS MEJORADA:
  - ✅ idTransaccion: Se muestra directamente el campo de la API (sin .numberLong)
  - ✅ Estados con colores pastel:
    * estadoVenta = "" (vacío) → [VIGENTE] en verde pastel (#d4edda)
    * estadoVenta = "[ANULADA]" → [ANULADA] en rojo pastel (#f8d7da)
  - ✅ Mapeo de filtros:
    * Usuario selecciona "[VIGENTE]" → API recibe estadoVenta = ""
    * Usuario selecciona "[ANULADA]" → API recibe estadoVenta = "[ANULADA]"
  - ✅ KPIs actualizados para reconocer correctamente estados vigentes y anulados
  - ✅ Botones de anulación solo aparecen para ventas vigentes (estadoVenta vacío)
  - ✅ Anulación en lote actualizada para nueva lógica de estados
  
  🔧 MÉTODOS ACTUALIZADOS v2.3:
  - fetchCatalogs(): Usa operación real de catálogos
  - getBranchNameById(): Adaptado para nombres directos de sucursales
  - handleSearchUnified(): CORREGIDO - Aplica paginación local después de búsqueda
  - setupLocalPagination(): NUEVO - Configura paginación local correctamente
  - updateLocalPagination(): CORREGIDO - Gestiona slice de datos por página
  - fetchFirstPageOnly(): CORREGIDO - Aplica paginación en carga inicial
  - formatDateForAPI(): Convierte YYYY-MM-DD a DD-MM-YYYY
  - validateDateRange(): Validación mejorada de rangos de fecha
  
  📊 ESTRUCTURA DE CATÁLOGOS v2.3:
  {
    "result": {
      "sucursales": ["SUCURSAL DE EJEMPLO", "Servidor Azure", ...],
      "estadosVenta": ["[CERRADA]", "[ABIERTA]"],
      "estadosCierreVenta": ["[VIGENTE]", "[ANULADA]"],
      "estadosMediosPago": ["[EFECTIVO]", "[TARJETA DE DÉBITO]", ...]
    }
  }
  
  📊 ESTRUCTURA DE PARÁMETROS API CON MAPEO DE ESTADOS:
  {
    "Parameters": {
      "page": 0,
      "limit": 20,
      "fechaDesde": "01-01-2025",     // DD-MM-YYYY
      "fechaHasta": "30-06-2025",     // DD-MM-YYYY  
      "nombreSucursal": "Servidor Azure - BD_MCONTROL_V_12",
      "nombreVendedor": "Soporte: CentralGym App",
      "estadoVenta": "",              // VIGENTE = cadena vacía
      // "estadoVenta": "[ANULADA]",  // ANULADA = tal como viene
      "rutCliente": "12345678-9",
      "idTransaccion": 485,
      "nombreCliente": "Juan Pérez"
    }
  }
  
  🎨 MAPEO DE ESTADOS v2.3:
  FILTRO UI          API PARÁMETRO       CAMPO EN DATOS      MOSTRAR EN TABLA
  ========================================================================
  [VIGENTE]    →     estadoVenta=""  →   estadoVenta=""   →  [VIGENTE] (verde pastel)
  [ANULADA]    →     estadoVenta="[ANULADA]" → estadoVenta="[ANULADA]" → [ANULADA] (rojo pastel)
  
  🔍 BÚSQUEDA INTELIGENTE:
  - Números: Se interpretan como ID de transacción
  - Formato RUT (con guión): Se busca en rutCliente
  - Texto libre: Se busca en nombreCliente
  
  🏗️ ARQUITECTURA DE FILTROS:
  - FILTROS BÁSICOS: Usan endpoint real → Nuevos datos desde servidor
  - FILTROS AVANZADOS: Trabajan en memoria → Filtran datos ya cargados
  - PAGINACIÓN: Local sobre datos en memoria → Sin consultas adicionales
  
  🔧 PROBLEMA DE PAGINACIÓN RESUELTO v2.3:
  - ❌ ANTES: Al hacer búsqueda, se mostraban TODOS los registros en tabla
  - ✅ DESPUÉS: Búsqueda obtiene todos los datos pero muestra solo 20 por página
  - 🔄 FLUJO CORREGIDO: fetchAll → setupPagination → updatePagination → mostrar slice
  
  ⚡ RENDIMIENTO OPTIMIZADO:
  - Carga inicial: Solo últimas 20 ventas (ultra-rápida)
  - Búsquedas: Obtienen todos los registros filtrados + aplican paginación local
  - Paginación: Slice local sin consultas al servidor (CORREGIDO)
  - Gráficos: Usan todos los datos filtrados, no solo página actual
  - KPIs: Calculados sobre todos los datos, no solo página actual
  
  🔄 MIGRACIÓN ESTRUCTURA PETICIONES v2.4 - APLICADA:
  ========================================================
  
  ✅ PETICIONES ACTUALIZADAS (formato anterior → nuevo formato):
  
  1. createGymSalesRecordsExportData():
     ❌ ANTES: token y operation en headers
     ✅ DESPUÉS: Header.Operacion y Auth.Token en body
     📍 Endpoint: "/v1/sales/get/gymsales/reportformat"
  
  2. fetchCatalogs():
     ❌ ANTES: token y operation en headers  
     ✅ DESPUÉS: Header.Operacion y Auth.Token en body
     📍 Endpoint: "/v1/sales/get/gymsalesviewcatalogs"
  
  3. fetchRecords():
     ❌ ANTES: token y operation en headers
     ✅ DESPUÉS: Header.Operacion y Auth.Token en body
     📍 Endpoint: "/v1/sales/get/gymsales"
  
  4. fetchAllFilteredRecordsForSearch():
     ❌ ANTES: token y operation en headers
     ✅ DESPUÉS: Header.Operacion y Auth.Token en body
     📍 Endpoint: "/v1/sales/get/gymsales"
  
  5. fetchFirstPageOnly():
     ❌ ANTES: token y operation en headers
     ✅ DESPUÉS: Header.Operacion y Auth.Token en body
     📍 Endpoint: "/v1/sales/get/gymsales"
  
  📋 NUEVA ESTRUCTURA DE BODY (todas las peticiones):
  {
    "Header": {
      "Operacion": "/v3/user/socios/consultar"
    },
    "Auth": {
      "Token": "eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9...."
    },
    "Parameters": {}, // Parámetros o filtros de la consulta
    "Body": {}        // Datos para insertar o trabajar
  }
  
  🛠️ COMPATIBILIDAD CON OnLineSalesDashboard:
  - Ambos componentes ahora usan la misma estructura de peticiones
  - Mantiene consistencia en toda la aplicación
  - Resuelve errores HTTP 404 por acceso no válido
  - Backend espera autenticación en body, no en headers
*/
</script>

<style></style> 
<style></style> 